<template>
  <div
    v-if="currentLocation"
    class="Realisation Process"
  >
    <div
      v-if="isForbidden"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        401
      </h2>
      <div>{{ $t('Process.401-cpo') }}</div>
    </div>
    <div
      v-else-if="notFound"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        404
      </h2>
      <div>{{ $t('Process.404-notFound') }}</div>
    </div>
    <div
      v-else
      class="d-flex justify-content-center flex-gap-2"
    >
      <ProcessMap
        v-if="!isPrivateProcess"
        :key="componentKey"
        :connection-enabled="isLocationConnected"
        :class="['process-map', { 'hidden': mapHidden }, { 'active': getShowMap }, { isSticky }]"
        :asset-process-type="assetProcessType"
        :map-hidden="mapHidden"
        @isLocationConnected="event => isLocationConnected = event"
        @currentRecord="record => (currentRecord = record, correction = false)"
        @feedback="event => (feedback = event.feedback, feedbackMessage = event.message)"
        @handleInput="handleInput"
        @handleCancelChange="event => locked = event"
      />
      <div
        v-if="!isPrivateProcess"
        class="process-main"
      >
        <RealisationHeader
          :record="record"
          :feedback="feedback"
          :feedback-message="feedbackMessage"
          :step="step"
          :record-step="currentStep"
          :may-edit="mayEdit && isCompletedStep"
          @dismissed="(feedback = null, feedbackMessage = '')"
        >
          <FlowInteraction
            :first="currentStep === 1"
            :last="isFinalStep"
            :step="step"
            :current-step="currentStep"
            :locked="locked"
            :disabled="disabled"
            :completed="completed"
            :is-completed-step="isCompletedStep"
            :disable-next-btn="disableNextBtn"
            :may-edit="mayEdit"
            :open-for-change="openForChange"
            :record="record"
            :location-connected="true"
            @handleNext="handleNext"
            @handlePrevious="handlePrevious"
            @handleSubmitAndNext="handleSubmitAndNext"
            @handleCancelChange="handleCancelChange"
            @handleStartCorrection="handleStartCorrection"
            @handleStartChange="handleStartChange"
            @handleComplete="handleComplete"
            @handleSubmit="saveRecord"
          />
        </RealisationHeader>
        <router-view
          :key="$route.path"
          ref="form"
          class="process-form"
          @isLocationConnected="event => isLocationConnected = event"
          @currentRecord="record => (currentRecord = record, correction = false)"
          @feedback="event => (feedback = event.feedback, feedbackMessage = event.message, locked = false)"
          @handleInput="handleInput"
          @handleCancelChange="handleCancelChange"
          @isSaving="event => isSaving = event"
        />
      </div>
      <div
        v-else
        class="process-main"
      >
        <RealisationHeader
          :record="record"
          :feedback="feedback"
          :feedback-message="feedbackMessage"
          :step="privateCurrentStep"
          :record-step="privateCurrentStep"
          :may-edit="privateMayEdit && privateIsCompletedStep"
          @dismissed="(feedback = null, feedbackMessage = '')"
        >
          <FlowInteraction
            :first="privateCurrentStep === 1"
            :last="privateIsFinalStep"
            :step="privateCurrentStep"
            :current-step="privateCurrentStep"
            :locked="locked"
            :disabled="disabled"
            :completed="completed"
            :is-completed-step="privateIsCompletedStep"
            :disable-next-btn="disableNextBtn"
            :may-edit="privateMayEdit"
            :open-for-change="openForChange"
            :record="record"
            :location-connected="true"
            @handleNext="() => $refs.privateForm && $refs.privateForm.handleNext()"
            @handlePrevious="() => $refs.privateForm && $refs.privateForm.handlePrevious()"
            @handleSubmitAndNext="() => $refs.privateForm && $refs.privateForm.handleSubmitAndNext()"
            @handleCancelChange="() => $refs.privateForm && $refs.privateForm.handleCancelChange()"
            @handleStartCorrection="() => $refs.privateForm && $refs.privateForm.handleStartCorrection()"
            @handleStartChange="() => $refs.privateForm && $refs.privateForm.handleStartChange()"
            @handleComplete="() => $refs.privateForm && $refs.privateForm.handleComplete()"
            @handleSubmit="() => $refs.privateForm && $refs.privateForm.saveRecord()"
          />
        </RealisationHeader>
        <router-view
          :key="$route.path"
          ref="privateForm"
          class="process-form"
          @feedback="event => (feedback = event.feedback, feedbackMessage = event.message)"
          @lockUpdate="event => locked = event"
          @isSaving="event => isSaving = event"
        />
      </div>
      <RealisationSidebar
        :uuid="uuid || ''"
        :disabled="disabled || locked"
        :class="{ 'private': isPrivateProcess }"
        :style="getShowMap && mapHidden ? 'display:none' : ''"
      />
      <Comments :record="record" />
    </div>
    <div
      v-if="isSaving"
      class="spinner-full-window d-flex"
    >
      <b-spinner
        variant="primary"
        class="spinner"
      />
    </div>
  </div>
  <div
    v-else
    class="spinner-full-window d-flex"
  >
    <b-spinner
      variant="primary"
      class="spinner"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getProcessByUuid, fetchBaseAssetProcess, fetchAssetByLocationId, setSelectedProcess } from '@/services/processes'
import RealisationSidebar from '@/components/realisation/RealisationSidebar';
import RealisationHeader from '@/components/realisation/RealisationHeader.vue';
import FlowInteraction from '@/components/flow/FlowInteraction.vue';
import Comments from '@/components/Comments'
import ProcessMap from '@/views/ProcessMap';
import BaseStep from '@/views/processes/BaseStep.vue';

export default {
  name: 'Process',
  components: {
    RealisationSidebar,
    RealisationHeader,
    FlowInteraction,
    Comments,
    ProcessMap
  },
  mixins: [BaseStep],
  data() {
    return {
      isLocationConnected: true,
      filteringStorePoints: [
        'chargingpoints/filterChargingPoints',
        'realisations/filterRealisations',
        'requests/filterRequests'
      ],
      processType: '',
      //record: null,
      isForbidden: false,
      notFound: false,
      componentKey: 0,
      isSaving: false,
      mapHidden: false,
      isSticky: false
    };
  },
  computed: {
    ...mapGetters('user', ['canAccessRealisationProcesses', 'hasRole', 'canAccessPrivateProcesses']),
    ...mapGetters('tenant', ['getActiveProcessTypes', 'getSteps']),
    ...mapGetters('processes', ['getSelectedProcess', 'getSelectedAsset', 'getCurrentProcessType', 'getShowMap', 'getIsSavingProcess']),


    isNewProcess() {
      return this.$route.name.startsWith('new-');
    },

    currentLocation () {
      return this.getSelectedProcess?.CurrentLocation || this.getSelectedProcess?.values?.Location
    },
    isSelectedProcess() {
      return this.getSelectedProcess?.locationId?.full === this.$route.params.locationId
        || !this.isNewProcess && (this.getSelectedProcess?.uuid === this.$route.params.uuid)
    },
    assetProcessType() {
      return this.$route.params.type &&
        this.getActiveProcessTypes.find(process => {
          return (process.routeType === this.$route.params.type) ||
                 (process.label.toLowerCase() === this.$route.params.type)
        })
    },
    isPrivateProcess () {
      return this.getSelectedProcess?.processType?.value === 'private'
    },
    isActiveAssetProcessCancelled () {
      return !this.isPrivateProcess && this.getSelectedAsset.processes.at(0)?.processStatus?.status === 'cancelled'
    },
    userIdentity () {
      const identity = this.$auth.user['https://evtools.nl/identity']
      return identity?.uuid ? identity : {}
    },
    currentProcessRouteType () {
      return this.getActiveProcessTypes?.find(type => type.routeType === this.$route.params?.type)?.value
    },
    isNewPrivateProcess () {
      return this.$route.name === 'new-private'
    },
    lastStep () {
      return this.getSteps?.at(-1)?.step
    },
    isFinalStep () {
      return this.currentStep === this.lastStep
    },
    privateCurrentStep () {
      return +this.$route.query?.step || this.record?.processStatus?.currentStep || 1
    },
    privateIsFinalStep () {
      if (!this.getSteps?.length) return false
      const steps = [...this.getSteps].sort((a, b) => Number(a.short) - Number(b.short))
      return Number(steps.at(-1).step) === this.privateCurrentStep
    },
    privateIsCompletedStep () {
      const stepData = this.getStepDataByNumber({ number: this.privateCurrentStep })
      return this.completedUuids.includes(stepData?.uuid)
    },
    privateMayEdit () {
      const stepData = this.getStepDataByNumber({ number: this.privateCurrentStep })
      return !this.statusLocked && (this.hasRole({ role: 'admin' }) || stepData?.access?.edit?.some(role => this.hasRole({ role })))
    }
  },
  watch: {
    $route: {
      async handler () {
        if (this.isNewPrivateProcess) {
          this.setNewPrivateProcess()
        }

        this.feedback = null;
        this.feedbackMessage = ''
        //this.locked = false

        this.$store.dispatch('processes/setCurrentProcessType', {
          processType: this.getSelectedProcess?.processType?.value || 'realisation'
        })
      }
    },
    getShowMap: {
      handler (value) {
        if (!value) return this.isSticky = false

        setTimeout(() => {
          this.isSticky = value
        }, 300)
      }
    },
    getIsSavingProcess: {
      handler (isSaving) {
        this.isSaving = isSaving
      }
    }
  },

  async created() {
    this.mapHidden = window.innerWidth <= 2100
    // Remove Realisation process from store and reset possible selectedProcess //
    this.$store.dispatch('realisations/setSelectedRecord', { record: null })
    this.$store.dispatch('processes/setSelectedProcess', { process: null })

    const token = await this.$auth.getTokenSilently()
    if (this.isNewProcess) {
      const locationId = this.$route.params.locationId

      if (this.assetProcessType) {
        await fetchBaseAssetProcess({ token, locationId, processType: this.assetProcessType })
          .catch(err => console.log(err))
      } else {
        this.setNewPrivateProcess()
      }
    } else {
      const uuid = this.$route.params.uuid
      if (!uuid) return

      const process = await getProcessByUuid({ token, uuid })
        .catch(err => console.log(err))

      if (!process || process.errorMessage) {
        this.notFound = true
      }

      if (process && !this.isPrivateProcess && this.userIdentity?.uuid) {
        const category = this.userIdentity.category
        const processCpo = process.process?.ChargePointOperator
        const processMunicipality = process.MunicipalityCode

        if (
          (category === 'cpo' && processCpo !== this.userIdentity?.uuid) ||
          (category === 'municipality' && processMunicipality !== this.userIdentity.code)
        ) {
          return this.isForbidden = true
        }
      }
    }

    const selectedAsset = this.getSelectedAsset?.locationId === this.getSelectedProcess?.locationId?.full
    //console.log('Process', selectedAsset, this.getSelectedProcess, this.getSelectedAsset)
    if (!selectedAsset && this.getSelectedProcess) {
      await fetchAssetByLocationId({ token, locationId: this.getSelectedProcess?.locationId?.full })
        .catch(err => console.log(err))
    }

    this.$store.dispatch('processes/setCurrentProcessType', {
      processType: this.getSelectedProcess?.processType?.value || 'realisation'
    })

    //this.record = this.getSelectedProcess
    this.componentKey++

    const code = this.record.MunicipalityCode
    this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })

    // If new process route, redirect if process type doesn't match //
    if (this.isNewProcess && !this.$router.currentRoute.name.includes(this.getSelectedProcess?.processType?.value)) {
      const processType = this.getSelectedProcess?.processType;
      if (processType){
        this.$router.replace({
          name: `new-${processType?.value}`
        });
      }
    }

    if (this.isNewProcess && this.isActiveAssetProcessCancelled) {
      this.$store.dispatch('tenant/setToast', {
        message: this.$i18n.t('LocationList.cancelledProcessMessage'),
        error: false,
        variant: 'warning',
        delay: 1000
      });
    }

    if (this.isNewProcess || this.$route.params.step === this.currentStep) return;

    if (this.$route.params?.uuid && this.getSelectedProcess && !(this.$route.params.step || this.$route.query.step)) {
      const processType = this.getSelectedProcess.processType

      if (['private'].includes(processType.value)) {
        // @processStatus.currentStep = 100 => completed process //
        const currentStep = this.currentStep === 100 ? 9 : this.currentStep
        if (currentStep === +this.$route.query.step) return;
        this.$router.replace({
          name: `${processType.value}Steps`,
          params: { type: processType.routeType, uuid: this.$route.params.uuid },
          query: { step: currentStep }
        });
      } else {
        this.$router.replace({
          name: `${processType.value}Step${this.currentStep}`,
          params: { type: processType.routeType, step: this.currentStep }
        });
      }
    }
  },
   mounted() {
    window.addEventListener('resize', this.setMapState)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setMapState)
  },
  methods: {
    setMapState (e) {
      this.mapHidden = e.target.innerWidth < 2100
    },
    async setNewPrivateProcess () {
      const data = {
        process: {
          Application: {},
          Object: {}
        },
        processType: {
          acronym: 'PRP',
          label: 'Privaatproces',
          routeType: 'privaat',
          value: 'private'
        },
        Municipality: '0000'
      };
      await setSelectedProcess(data)
    }
  }
};
</script>
